/** Loading a project's derived data (reveal plans, layers, fonts, hands) over HTTP. */

import type { GlyphSet, LoadedFont } from "./fonts";
import { parseFont } from "./fonts";
import type { Asset, Project } from "./project.gen";
import type { PlanStroke } from "./strokes";

export interface PlanGroup {
  id: string;
  bbox: [number, number, number, number];
  strokes: PlanStroke[];
}

export interface RevealPlan {
  version: number;
  kind: "raster" | "svg";
  size: [number, number];
  layers: { outline: string; fill: string };
  outline: { groups: PlanGroup[] };
  fill: { spacing: number; groups: PlanGroup[] };
  stats: Record<string, number>;
}

export interface ImageRes {
  plan: RevealPlan;
  outline: ImageBitmap;
  fill: ImageBitmap;
}

export interface HandRes {
  img: ImageBitmap;
  anchor: { x: number; y: number };
}

export interface Resources {
  images: Map<string, ImageRes>;
  fonts: Map<string, LoadedFont>;
  hands: Map<string, HandRes>;
  /** background images, as uploaded (not analysed) */
  backgrounds: Map<string, ImageBitmap>;
}

export const derivedDir = (assetId: string) => `derived/${assetId}`;

async function getJSON<T>(url: string): Promise<T> {
  const r = await fetch(url);
  if (!r.ok) throw new Error(`${r.status} ${url}`);
  return (await r.json()) as T;
}

async function getBitmap(url: string): Promise<ImageBitmap> {
  const r = await fetch(url);
  if (!r.ok) throw new Error(`${r.status} ${url}`);
  // no colour-space conversion: identical pixels on every machine
  return createImageBitmap(await r.blob(), { colorSpaceConversion: "none", premultiplyAlpha: "default" });
}

export function emptyResources(): Resources {
  return { images: new Map(), fonts: new Map(), hands: new Map(), backgrounds: new Map() };
}

/** A reduced copy of a layer image for the editor (at most 1200 px, WebP; the server makes it from
 *  the full PNG on first request). Final renders always load the full PNG. */
export const editorLayer = (name: string) => name.replace(/\.png$/, ".editor.webp");

/**
 * Load (or reload) one asset's render data into `res`. `version` busts HTTP caches after a
 * re-analysis (and lets the browser cache the files for good). `editor` loads the reduced layer
 * copies, falling back to the full ones. Audio assets have nothing to load here.
 */
export async function loadAsset(a: Asset, base: string, res: Resources, version?: string | number, asBackground = false, editor = false): Promise<void> {
  const b = base.replace(/\/$/, "");
  if (asBackground) {
    res.backgrounds.set(a.id, await getBitmap(`${b}/${a.file}`));
    return;
  }
  const d = `${b}/${derivedDir(a.id)}`;
  const v = version !== undefined ? `?v=${encodeURIComponent(String(version))}` : "";
  if (a.kind === "image" || a.kind === "svg") {
    const plan = await getJSON<RevealPlan>(`${d}/plan.json${v}`);
    const layer = (name: string) =>
      editor ? getBitmap(`${d}/${editorLayer(name)}${v}`).catch(() => getBitmap(`${d}/${name}${v}`)) : getBitmap(`${d}/${name}${v}`);
    const [outline, fill] = await Promise.all([layer(plan.layers.outline), layer(plan.layers.fill)]);
    res.images.set(a.id, { plan, outline, fill });
  } else if (a.kind === "font") {
    const [buf, glyphs] = await Promise.all([
      fetch(`${b}/${a.file}`).then((r) => {
        if (!r.ok) throw new Error(`${r.status} ${a.file}`);
        return r.arrayBuffer();
      }),
      getJSON<GlyphSet>(`${d}/glyphs.json${v}`),
    ]);
    res.fonts.set(a.id, { font: parseFont(buf), glyphs });
  } else if (a.kind === "hand") {
    res.hands.set(a.id, { img: await getBitmap(`${b}/${a.file}`), anchor: a.anchor ?? { x: 0, y: 0 } });
  }
}

export async function loadResources(project: Project, base: string): Promise<Resources> {
  const res = emptyResources();
  const used = usedAssetIds(project);
  await Promise.all(project.assets.filter((a) => used.has(a.id)).map((a) => loadAsset(a, base, res)));
  const bg = project.board.background;
  const bgAsset = bg.kind === "image" && bg.assetId ? project.assets.find((a) => a.id === bg.assetId) : undefined;
  if (bgAsset) await loadAsset(bgAsset, base, res, undefined, true);
  return res;
}

/** Asset ids the visual elements reference (audio is handled by the mixer, not loaded here). */
export function usedAssetIds(project: Project): Set<string> {
  const s = new Set<string>();
  for (const e of project.elements) {
    if (e.image) s.add(e.image.assetId);
    if (e.text) s.add(e.text.fontAssetId);
    if (e.enter.handId) s.add(e.enter.handId);
  }
  s.add(project.defaults.handId);
  if (project.captions.enabled) {
    const f = project.captions.fontAssetId ?? project.assets.find((a) => a.kind === "font")?.id;
    if (f) s.add(f);
  }
  return s;
}
